import { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Copy, Check } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";

// Reusable media library. Pass onPick(url) to use as a picker.
export default function MediaLibrary({ onPick }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await load();
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => { await api.delete(`/media/${id}`); load(); };
  const copy = (url) => { navigator.clipboard?.writeText(mediaUrl(url)); setCopied(url); setTimeout(() => setCopied(""), 1500); };

  return (
    <div data-testid="media-library">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold">Media Library</h2>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-brand-magenta px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">
          <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload"}
          <input ref={fileRef} data-testid="media-upload-input" type="file" accept="image/*" multiple hidden onChange={upload} />
        </label>
      </div>
      {items.length === 0 && <p className="text-sm text-slate-400">No images yet. Upload to get started.</p>}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id}`} className="group overflow-hidden rounded-xl border border-slate-200 bg-white">
            <div className="relative aspect-[4/3] cursor-pointer bg-slate-100" onClick={() => onPick && onPick(m.url)}>
              <img src={mediaUrl(m.url)} alt={m.filename} className="h-full w-full object-cover" />
              {onPick && <div className="absolute inset-0 flex items-center justify-center bg-brand-ink/0 text-sm font-semibold text-white opacity-0 transition-all group-hover:bg-brand-ink/50 group-hover:opacity-100">Select</div>}
            </div>
            <div className="flex items-center justify-between gap-1 p-2">
              <span className="truncate text-xs text-slate-500" title={m.filename}>{m.filename}</span>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => copy(m.url)} title="Copy URL" className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-brand-magenta">
                  {copied === m.url ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
                <button onClick={() => remove(m.id)} title="Delete" className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
